import { useMemo } from 'react';
import { Platform } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis, { getAIUsageStats, getCompanyList } from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useLearningMode } from '../hooks/useLearningMode';
import { hasActivePaidEntitlementFromSubscriptionPayload } from '@navigation/screens/MidLevel/UpgradeScreen/utils/subscriptionEntitlementFromPayload';
import { assessCompanyBilling } from './companyBillingStatus';

// Types
export interface BillingInfo {
  isActive?: boolean;
  status?: string;
  /** Backend / IAP may send a string (e.g. "Free Plan") or a Stripe-like object */
  product?:
    | string
    | {
        name?: string;
        id?: string;
        metadata?: {
          ai_call_limit?: number;
          [key: string]: any;
        };
      };
  [key: string]: any;
}

/** Lowercase plan label for free/paid checks; supports product as string or { name }. */
export const billingPlanProductNameLower = (plan: BillingInfo | undefined): string => {
  if (!plan) return '';
  const p = plan.product;
  if (typeof p === 'string') return p.toLowerCase().trim();
  if (p && typeof p === 'object' && typeof p.name === 'string') {
    return p.name.toLowerCase().trim();
  }
  return '';
};

/** Normalizes a plan name to its core tier and interval for cross-platform matching. */
export const getCanonicalPlanName = (name: string | null | undefined): { tier: number, isAnnual: boolean } => {
  const n = (name || '').toLowerCase();
  
  let tier = 6;
  if (n.includes('free') || n.includes('starter')) tier = 0;
  else if (n.includes('premium')) tier = 1;
  else if (n.includes('professional') && n.includes('basic')) tier = 2;
  else if (n.includes('professional')) tier = 3;
  else if (n.includes('organisation') || n.includes('organization')) tier = 4;
  else if (n.includes('enterprise') || n.includes('bronze')) tier = 5;

  const isAnnual = n.includes('annual') || n.includes('year');
  
  return { tier, isAnnual };
};

/** Checks if two plan names represent the same tier and interval. */
export const isSamePlan = (plan1: string | null | undefined, plan2: string | null | undefined): boolean => {
  if (!plan1 || !plan2) return false;
  const p1 = getCanonicalPlanName(plan1);
  const p2 = getCanonicalPlanName(plan2);
  return p1.tier === p2.tier && p1.isAnnual === p2.isAnnual;
};

/** Human-readable product label (preserves API casing). EXEMPT_PRODUCT maps to SmartSaaS for display. */
export const billingPlanProductDisplayName = (plan: BillingInfo | undefined): string | null => {
  if (!plan) return null;
  const p = plan.product;
  if (typeof p === 'string') {
    const t = p.trim();
    if (!t) return null;
    if (t === 'EXEMPT_PRODUCT' || t.toLowerCase().includes('exempt')) return 'SmartSaaS';
    return t;
  }
  if (p && typeof p === 'object' && typeof p.name === 'string') {
    const t = p.name.trim();
    if (!t) return null;
    if (t === 'EXEMPT_PRODUCT' || t.toLowerCase().includes('exempt')) return 'SmartSaaS';
    return t;
  }
  return null;
};

/**
 * Unwrap subscriptionPlan React Query cache (getSubscriptions body) so status/product match PlanComponent.
 */
export const unwrapSubscriptionPlanCache = (raw: unknown): BillingInfo | undefined => {
  if (raw == null || raw === 'No subscriptions active') return undefined;
  if (typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const inner = r.data;
  if (inner && typeof inner === 'object') {
    if (Array.isArray(inner)) {
      const first = inner[0];
      if (first && typeof first === 'object') {
        return { ...r, ...(first as object) } as BillingInfo;
      }
      return r as BillingInfo;
    }
    const d = inner as Record<string, unknown>;
    const core = d.data;
    if (core && typeof core === 'object' && !Array.isArray(core)) {
      return { ...r, ...d, ...core } as BillingInfo;
    }
    if (Array.isArray(core)) {
      const first = core[0];
      if (first && typeof first === 'object') {
        return { ...r, ...d, ...(first as object) } as BillingInfo;
      }
    }
    return { ...r, ...d } as BillingInfo;
  }
  return r as BillingInfo;
};

export interface AIUsageStats {
  today: number;
  thisWeek: number;
  thisMonth: number;
  currentCount: number;
  limit: number;
  totalUsage: number; // percentage
  breakdown?: Array<{
    endpoint: string;
    calls: number;
  }>;
  period?: {
    day: string;
    week: number;
    month: string;
  };
}

/**
 * React Query key for `apis.getSubscriptions()` cache.
 * Includes `sessionUserId` so a different account never inherits the previous user's subscription payload
 * (placeholderData / same key after logout+login).
 */
export function getSubscriptionPlanQueryKey(
  sessionUserId: string | null | undefined,
  companyNumber?: string | null,
): readonly unknown[] {
  if (sessionUserId == null || sessionUserId === '') {
    return ['subscriptionPlan', '__idle__'] as const;
  }
  if (companyNumber != null && companyNumber !== '') {
    return ['subscriptionPlan', sessionUserId, companyNumber] as const;
  }
  return ['subscriptionPlan', sessionUserId] as const;
}

/**
 * When key is `['subscriptionPlan', userId, companyId]`, also walk the default org cache at `['subscriptionPlan', userId]`.
 */
export function getSubscriptionPlanQueryKeyFallback(
  subscriptionPlanQueryKey: readonly unknown[],
): readonly unknown[] | null {
  if (
    Array.isArray(subscriptionPlanQueryKey) &&
    subscriptionPlanQueryKey[0] === 'subscriptionPlan' &&
    typeof subscriptionPlanQueryKey[1] === 'string' &&
    subscriptionPlanQueryKey[1] !== '__idle__' &&
    subscriptionPlanQueryKey.length >= 3
  ) {
    return ['subscriptionPlan', subscriptionPlanQueryKey[1]] as const;
  }
  return null;
}

/**
 * Canonical React Query observer for `apis.getSubscriptions()` — same key/options as PlanComponent.
 * Screens must use this (or hooks built on it) instead of `getQueryData` alone so UI re-renders when the cache updates.
 */
export const useSubscriptionPlanQuery = (companyNumber?: string | null) => {
  const queryClient = useQueryClient();
  const sessionUserId = useSelector(
    (state: DefaultRootState) => state.promiseData?.data?.userData?.id ?? null,
  ) as string | null;

  const subscriptionPlanQueryKey = useMemo(
    () => getSubscriptionPlanQueryKey(sessionUserId, companyNumber),
    [sessionUserId, companyNumber],
  );

  return useQuery({
    queryKey: subscriptionPlanQueryKey,
    queryFn: async () => apis.getSubscriptions(),
    enabled: Boolean(sessionUserId),
    placeholderData: (previousData) => {
      if (previousData !== undefined) return previousData;
      if (!sessionUserId) return undefined;
      const scopedKey = getSubscriptionPlanQueryKey(sessionUserId, companyNumber);
      const scoped = queryClient.getQueryData<unknown>(scopedKey);
      const baseKey = getSubscriptionPlanQueryKey(sessionUserId, null);
      const base = queryClient.getQueryData<unknown>(baseKey);
      return (scoped ?? base) as Awaited<ReturnType<typeof apis.getSubscriptions>> | undefined;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: Platform.OS !== 'web',
    refetchOnReconnect: true,
  });
};

/** Single observer: raw `getSubscriptions()` payload + unwrapped row (avoids triple `useSubscriptionPlanQuery` on one screen). */
export const useSubscriptionPlanData = (companyNumber?: string | null) => {
  const q = useSubscriptionPlanQuery(companyNumber);
  const unwrapped = useMemo(() => unwrapSubscriptionPlanCache(q.data), [q.data]);
  return { ...q, unwrapped };
};

export function computePaidSubscriptionAccess(
  raw: unknown,
  unwrapped: BillingInfo | undefined,
  isLearningMode: boolean,
): boolean {
  if (isLearningMode) return false;
  if (raw == null) return false;
  if (!hasActivePaidEntitlementFromSubscriptionPayload(raw)) return false;
  if (!unwrapped) return false;
  const planName = billingPlanProductNameLower(unwrapped);
  if (
    planName.includes('free') ||
    planName.includes('starter')
  ) {
    return false;
  }
  return true;
}

/**
 * Unwrapped subscription row for labels / Redux-shaped checks (same as PlanComponent after normalize).
 */
export const useCurrentPlan = (companyNumber?: string | null): BillingInfo | undefined => {
  const { unwrapped } = useSubscriptionPlanData(companyNumber);
  return unwrapped;
};

/**
 * Active paid access — aligned with `hasActivePaidEntitlementFromPayload` (Plan cards), not only flat `status` on unwrap.
 */
export const useHasActiveSubscription = (companyNumber?: string | null): boolean => {
  const isLearningMode = useLearningMode();
  const { data: raw, unwrapped } = useSubscriptionPlanData(companyNumber);
  return useMemo(
    () => computePaidSubscriptionAccess(raw, unwrapped, isLearningMode),
    [raw, unwrapped, isLearningMode],
  );
};

/**
 * True when the current context is on the free plan (or has no paid access).
 * Returns false only while the first plan fetch is in flight with nothing cached, so
 * paid users don't see free-plan locks flash on cold start; the backend enforces limits.
 */
export const useIsFreePlan = (companyNumber?: string | null): boolean => {
  const isLearningMode = useLearningMode();
  const { data: raw, unwrapped, isLoading } = useSubscriptionPlanData(companyNumber);
  const hasActiveSubscription = useMemo(
    () => computePaidSubscriptionAccess(raw, unwrapped, isLearningMode),
    [raw, unwrapped, isLearningMode],
  );

  return useMemo(() => {
    if (isLoading && raw === undefined) return false;
    if (!unwrapped) return true;
    const planName = billingPlanProductNameLower(unwrapped);
    return (
      planName.includes('free') ||
      planName.includes('starter') ||
      !hasActiveSubscription
    );
  }, [isLoading, raw, unwrapped, hasActiveSubscription]);
};

/**
 * Hook to get AI usage statistics
 * @param options - Configuration options
 * @param options.enabled - Whether to fetch stats (defaults to true)
 * @param options.staleTime - Cache time in ms (defaults to 2 minutes)
 * @returns AI usage stats with loading state
 */
export const useAIUsageStats = (options?: {
  enabled?: boolean;
  staleTime?: number;
}) => {
  const { enabled = true, staleTime = 2 * 60 * 1000 } = options || {};
  
  return useQuery({
    queryKey: ['aiUsageStats'],
    queryFn: async () => {
      const response = await getAIUsageStats();
      return response?.data || response;
    },
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });
};

/**
 * Hook to get AI usage stats for free plan users only
 * Automatically fetches only when user is on free plan
 * @param companyNumber - Optional company number for company context
 * @returns AI usage stats with loading state
 */
export const useAIUsageStatsForFreePlan = (companyNumber?: string | null) => {
  const isFreePlan = useIsFreePlan(companyNumber);
  
  return useAIUsageStats({
    enabled: isFreePlan,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to get current plan's AI call limit
 * @param companyNumber - Optional company number for company context
 * @returns AI call limit or default value
 */
export const useAICallLimit = (companyNumber?: string | null): number => {
  const currentPlan = useCurrentPlan(companyNumber);
  const isFreePlan = useIsFreePlan(companyNumber);
  
  return useMemo(() => {
    const product = currentPlan?.product;
    const limit = typeof product === 'object' ? product?.metadata?.ai_call_limit : undefined;
    // Return limit if defined, otherwise default based on plan type
    if (limit !== undefined && limit !== null) {
      return limit;
    }
    // Default limits
    return isFreePlan ? 5 : 1000; // Free plan: 5, Paid plan: 1000
  }, [currentPlan, isFreePlan]);
};

/**
 * Hook to get comprehensive billing information
 * Combines plan info, subscription status, and AI usage
 * @param companyNumber - Optional company number for company context
 * @returns Comprehensive billing information
 */
export const useBillingInfo = (companyNumber?: string | null) => {
  const currentPlan = useCurrentPlan(companyNumber);
  const hasActiveSubscription = useHasActiveSubscription(companyNumber);
  const isFreePlan = useIsFreePlan(companyNumber);
  const aiCallLimit = useAICallLimit(companyNumber);
  const { data: aiUsageStats, isLoading: aiUsageLoading } = useAIUsageStatsForFreePlan(companyNumber);
  
  return useMemo(() => {
    const product = currentPlan?.product;
    const currentUsage = aiUsageStats?.currentCount || aiUsageStats?.thisMonth || 0;
    const usagePercentage = aiCallLimit > 0 
      ? Math.min(Math.round((currentUsage / aiCallLimit) * 100), 100) 
      : 0;
    
    return {
      currentPlan,
      hasActiveSubscription,
      isFreePlan,
      aiCallLimit,
      aiUsage: {
        current: currentUsage,
        limit: aiCallLimit,
        percentage: usagePercentage,
        stats: aiUsageStats,
        isLoading: aiUsageLoading,
      },
      planName: (typeof product === 'string' ? product : product?.name) || null,
      planId: (typeof product === 'object' ? product?.id : null) || null,
    };
  }, [
    currentPlan,
    hasActiveSubscription,
    isFreePlan,
    aiCallLimit,
    aiUsageStats,
    aiUsageLoading,
  ]);
};

/**
 * Utility function to check if a plan name indicates a free plan
 * @param planName - Plan name to check
 * @returns boolean indicating if plan is free
 */
export const isFreePlanName = (planName: string | null | undefined): boolean => {
  if (!planName) return true;
  const lowerName = planName.toLowerCase();
  return lowerName.includes('free') ||
    lowerName.includes('starter')
};

/**
 * Utility function to check if plan status is active
 * @param plan - Plan object to check
 * @returns boolean indicating if plan is active
 */
export const isPlanActive = (plan: BillingInfo | null | undefined): boolean => {
  if (!plan) return false;
  return plan.isActive !== false &&
    plan.status !== 'cancelled' &&
    plan.status !== 'expired' &&
    plan.status !== 'inactive';
};

/**
 * Utility function to get company number from Redux state
 * @returns Company number or null
 */
export const useCompanyNumber = (): string | null => {
  const selectedProfileId = useSelector((state: DefaultRootState) => state.app.selectedProfile?.id);
  const company = useSelector((state: DefaultRootState) => 
    state.promiseData.data?.companyData || null
  );
  // Prefer selectedProfileId (what the UI usually follows) over promiseData (which can lag)
  return selectedProfileId || company?._id || company?.id || null;
};

/**
 * Hook that automatically uses company context
 * Uses companyNumber from Redux if available, falls back to user context
 * @returns Billing info with automatic company context
 */
export const useBillingInfoWithContext = () => {
  const companyNumber = useCompanyNumber();
  return useBillingInfo(companyNumber);
};

/**
 * Hook to check if user has active subscription with automatic company context
 * @returns boolean indicating if user has active paid subscription
 */
export const useHasActiveSubscriptionWithContext = (): boolean => {
  const companyNumber = useCompanyNumber();
  return useHasActiveSubscription(companyNumber);
};

/**
 * Hook to check if user is on free plan with automatic company context
 * @returns boolean indicating if user is on free plan
 */
export const useIsFreePlanWithContext = (): boolean => {
  const companyNumber = useCompanyNumber();
  return useIsFreePlan(companyNumber);
};

/**
 * Hook to determine if a specific feature should be locked based on subscription status and usage limits.
 * @param itemCount - Current number of items (projects, agents, data folders, etc.)
 * @param freeLimit - Maximum number of items allowed on the free plan (defaults to 3)
 * @returns boolean indicating if the feature should be locked
 */
export const useSubscriptionLock = (itemCount: number, freeLimit: number = 3): boolean => {
  const companyNumber = useCompanyNumber();
  const isFreePlan = useIsFreePlan(companyNumber);
  const currentPlan = useCurrentPlan(companyNumber);

  const { data: companyListRaw } = useQuery({
    queryKey: ['companyList'],
    queryFn: getCompanyList,
    enabled: Boolean(companyNumber),
    staleTime: 1000 * 60 * 2,
  });

  const companyNeedsPayment = useMemo(() => {
    if (!companyNumber) return false;
    const list = Array.isArray(companyListRaw)
      ? companyListRaw
      : Array.isArray((companyListRaw as any)?.data)
        ? (companyListRaw as any).data
        : [];
    const company = list.find(
      (c: any) => String(c?._id || c?.id) === String(companyNumber),
    );
    if (!company) return false;
    return assessCompanyBilling(company).needsPayment;
  }, [companyNumber, companyListRaw]);

  return useMemo(() => {
    // Companies are always paid — hard-lock writes when billing is missing/failed
    if (companyNeedsPayment) return true;
    const isPlanExpired = currentPlan?.status === 'expired' || currentPlan?.status === 'cancelled';
    return (isFreePlan && itemCount >= freeLimit) || isPlanExpired;
  }, [companyNeedsPayment, isFreePlan, itemCount, freeLimit, currentPlan?.status]);
};

